import { useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeProvider';
import useTrialStore from '@/stores/TrialStore';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import CheckoutForm from '@/components/trial/CheckoutForm';
import TrialStepper from '@/components/trial/TrialStepper';

export default function TrialPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme } = useTheme();
  const client_secret = useTrialStore((state) => state.client_secret);
  const [trialData, setTrialData] = useState(null);

  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

  // Stripe Elements appearance configuration to match app theme
  const appearance = {
    theme: currentTheme === 'dark' ? 'night' : 'stripe',
    variables: {
      colorPrimary: 'hsl(193 100% 40.4%)',
      colorBackground: currentTheme === 'dark' ? '#0a0e13' : '#ffffff',
      colorText: currentTheme === 'dark' ? '#f1f5f9' : '#374151',
      colorDanger: currentTheme === 'dark' ? '#ef4444' : '#dc2626',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
    rules: {
      '.Input': {
        backgroundColor: currentTheme === 'dark' ? '#0a0e13' : '#ffffff',
        border:
          currentTheme === 'dark' ? '1px solid #374151' : '1px solid #d1d5db',
        borderRadius: '8px',
        color: currentTheme === 'dark' ? '#f1f5f9' : '#374151',
        fontSize: '14px',
        padding: '12px',
        transition:
          'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      },
      '.Input:focus': {
        borderColor: 'hsl(193 100% 40.4%)',
        boxShadow: '0 0 0 2px hsl(193 100% 40.4% / 0.2)',
        outline: 'none',
      },
      '.Input--invalid': {
        borderColor: currentTheme === 'dark' ? '#ef4444' : '#dc2626',
        boxShadow:
          currentTheme === 'dark'
            ? '0 0 0 2px rgba(239, 68, 68, 0.2)'
            : '0 0 0 2px rgba(220, 38, 38, 0.2)',
      },
      '.Input::placeholder': {
        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
      },
      '.Label': {
        color: currentTheme === 'dark' ? '#f1f5f9' : '#374151',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '6px',
      },
      '.Tab': {
        backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#f9fafb',
        border:
          currentTheme === 'dark' ? '1px solid #374151' : '1px solid #d1d5db',
        borderRadius: '8px',
        color: currentTheme === 'dark' ? '#d1d5db' : '#6b7280',
        fontSize: '14px',
        padding: '12px 16px',
        transition: 'all 0.2s ease-in-out',
      },
      '.Tab:hover': {
        backgroundColor: currentTheme === 'dark' ? '#374151' : '#f3f4f6',
        color: currentTheme === 'dark' ? '#f1f5f9' : '#374151',
      },
      '.Tab--selected': {
        backgroundColor: 'hsl(193 100% 40.4%)',
        borderColor: 'hsl(193 100% 40.4%)',
        color: '#ffffff',
      },
      '.TabIcon': {
        fill: 'currentColor',
      },
      '.Block': {
        backgroundColor: currentTheme === 'dark' ? '#0a0e13' : '#ffffff',
        border:
          currentTheme === 'dark' ? '1px solid #374151' : '1px solid #d1d5db',
        borderRadius: '8px',
        padding: '16px',
      },
      '.CheckboxInput': {
        backgroundColor: currentTheme === 'dark' ? '#0a0e13' : '#ffffff',
        border:
          currentTheme === 'dark' ? '1px solid #374151' : '1px solid #d1d5db',
        borderRadius: '4px',
      },
      '.CheckboxInput:checked': {
        backgroundColor: 'hsl(193 100% 40.4%)',
        borderColor: 'hsl(193 100% 40.4%)',
      },
      '.Label--checkbox': {
        color: currentTheme === 'dark' ? '#f1f5f9' : '#374151',
        fontSize: '14px',
        fontWeight: '400',
      },
      '.Error': {
        color: currentTheme === 'dark' ? '#ef4444' : '#dc2626',
        fontSize: '14px',
      },
      '.PII': {
        backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#f9fafb',
        border:
          currentTheme === 'dark' ? '1px solid #374151' : '1px solid #d1d5db',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '8px',
      },
    },
  };

  const features = [
    'AI Autopilot that books appointments automatically',
    'Use your own Voice to send audio messages',
    'AI DM Copilot that guides your team on what to say next',
    'Integrated inbox for IG, FB & LinkedIn',
    'Measure what matters using our Pipeline analytics',
    'Unlimited AI replies',
    'Unlimited team members',
    'No long-term commitment. Cancel anytime.',
  ];

  useEffect(() => {
    // Check if user has valid trial data in location state
    if (!location.state || !location.state.trial_id) {
      toast.error(
        'Invalid access. Please start from the onboarding signup page.'
      );
      navigate('/onboarding/signup');
      return;
    }

    // Check if client_secret is not available
    if (!client_secret || client_secret.trim() === '') {
      toast.error(
        'Invalid access. Please start from the onboarding signup page.'
      );
      navigate('/onboarding/signup');
      return;
    }

    setTrialData(location.state);
  }, [location.state, navigate, client_secret]);

  if (!trialData) {
    return (
      <div className='container relative flex h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='flex h-screen overflow-hidden'>
      {/* Left side - 40% */}
      <div className='flex w-2/5 flex-col bg-background'>
        <ScrollArea className='h-full'>
          <div className='mx-auto flex w-full max-w-md flex-col justify-center space-y-6 px-4 py-8'>
            {/* Payment Header */}
            <div className='space-y-2'>
              <h1 className='text-3xl font-semibold tracking-tight'>
                Activate Your Revio Account
              </h1>
              <p className='text-base font-medium text-muted-foreground'>
                $597/month • Cancel anytime
              </p>
              <p className='text-sm text-muted-foreground'>
                Gain instant access to Revio, Autopilot, and all AI features
                right after checkout.
              </p>
            </div>

            {/* Payment Form */}
            <Card className='h-fit'>
              <CardContent className='pt-6'>
                {client_secret && stripePromise ? (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: client_secret,
                      appearance: appearance,
                    }}
                  >
                    <CheckoutForm />
                  </Elements>
                ) : (
                  <div className='py-8 text-center'>
                    <div className='flex items-center justify-center space-x-2 text-muted-foreground'>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      <p>Initializing payment form...</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </div>

      <Separator orientation='vertical' />
      {/* Right side - 60% */}
      <div className='flex w-3/5 items-center justify-center bg-background p-8'>
        <div className='mx-auto w-full max-w-lg space-y-8'>
          <TrialStepper currentStep={2} title='Payment' />
          <div className='space-y-6'>
            <div className='space-y-2'>
              <h2 className='text-2xl font-semibold'>Revio Monthly Plan</h2>
              <p className='text-base text-muted-foreground'>
                Everything you need to convert leads on autopilot
              </p>
            </div>

            <div className='space-y-1'>
              <p className='text-3xl font-bold'>$597/month</p>
            </div>

            <div className='space-y-4'>
              <p className='text-sm font-medium'>Your subscription includes:</p>
              <ul className='space-y-4'>
                {features.map((feature, index) => (
                  <li key={index} className='flex items-start space-x-3'>
                    <div className='mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary' />
                    <p className='text-sm text-muted-foreground'>{feature}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
